import type { Collector } from './types';
import { prop, safe, signal, unavailable, withTimeout } from './util';

/** Guess a keyboard layout from the characters on a few physical keys. */
export function guessLayout(keys: Record<string, string | undefined>): string {
  const { KeyQ, KeyW, KeyY, KeyZ, KeyE, KeyS } = keys;
  if (KeyQ === 'a' && KeyW === 'z') return 'AZERTY';
  if (KeyY === 'z' && KeyZ === 'y') return 'QWERTZ';
  if (KeyQ === "'" && KeyS === 'o') return 'Dvorak';
  if (KeyQ === 'q' && KeyE === 'f') return 'Colemak';
  if (KeyQ === 'q' && KeyW === 'w' && KeyY === 'y') return 'QWERTY';
  if (KeyQ && !/^[a-z']$/.test(KeyQ)) return 'Non-Latin layout';
  return 'Unknown';
}

/**
 * Smallest observed tick of performance.now(). Browsers coarsen it to resist timing attacks;
 * Firefox resistFingerprinting and Tor Browser coarsen it much further.
 */
export function measureTimerResolution(maxWallMs = 220): number | null {
  const start = Date.now();
  let last = performance.now();
  let min = Infinity;
  let ticks = 0;
  while (ticks < 20 && Date.now() - start < maxWallMs) {
    const t = performance.now();
    if (t !== last) {
      min = Math.min(min, t - last);
      last = t;
      ticks++;
    }
  }
  return Number.isFinite(min) ? min : null;
}

export function describeResolution(ms: number | null): string {
  if (ms === null) return 'coarser than 200 ms';
  if (ms < 0.02) return `${(ms * 1000).toFixed(1)} µs (fine-grained)`;
  if (ms < 1) return `${(ms * 1000).toFixed(0)} µs`;
  return `${ms.toFixed(2)} ms`;
}

export const miscCollector: Collector = {
  id: 'misc',
  title: 'Other',
  description: 'Keyboard layout, timer precision and page state at the moment of collection.',
  async collect() {
    const doc = typeof document === 'undefined' ? undefined : document;
    return Promise.all([
      safe('misc.referrer', 'Referrer', () =>
        doc
          ? signal('misc.referrer', 'Referrer', doc.referrer, {
              display: doc.referrer || '(empty: no referrer was sent, or you opened this page directly)',
            })
          : unavailable('misc.referrer', 'Referrer', 'No document'),
      ),

      safe('misc.focus', 'Page visibility & focus', () => {
        if (!doc) return unavailable('misc.focus', 'Page visibility & focus', 'No document');
        const value = { visibility: doc.visibilityState, hasFocus: doc.hasFocus() };
        return signal('misc.focus', 'Page visibility & focus', value, {
          display: `${value.visibility} · ${value.hasFocus ? 'focused' : 'not focused'}`,
        });
      }),

      safe('misc.keyboard', 'Keyboard layout', async () => {
        const kb = prop<{ getLayoutMap?: () => Promise<Map<string, string>> }>(navigator, 'keyboard');
        if (!kb?.getLayoutMap) return unavailable('misc.keyboard', 'Keyboard layout', 'Not supported (Chromium-only API)');
        const map = await withTimeout(kb.getLayoutMap(), 1000, 'getLayoutMap');
        const codes = ['KeyQ', 'KeyW', 'KeyE', 'KeyY', 'KeyZ', 'KeyA', 'KeyS', 'KeyM', 'Semicolon', 'BracketLeft', 'Backquote', 'IntlBackslash'];
        const keys = Object.fromEntries(codes.map((c) => [c, map.get(c)]));
        const layout = guessLayout(keys);
        return signal('misc.keyboard', 'Keyboard layout', { layout, keys }, {
          display: `${layout} · ${codes
            .filter((c) => keys[c] !== undefined)
            .map((c) => `${c}→${keys[c]}`)
            .join(' ')}`,
        });
      }),

      safe('misc.timer', 'Timer precision', () => {
        if (typeof performance === 'undefined') return unavailable('misc.timer', 'Timer precision', 'performance.now() not available');
        const ms = measureTimerResolution();
        const isolated = typeof crossOriginIsolated === 'boolean' ? crossOriginIsolated : null;
        return signal('misc.timer', 'Timer precision', { resolutionMs: ms === null ? 200 : Math.round(ms * 1e4) / 1e4, crossOriginIsolated: isolated }, {
          display: `performance.now() ticks every ${describeResolution(ms)}`,
        });
      }),
    ]);
  },
};
